import { useMemo } from 'react';
import { Field, useFormState, TextInput, required as requiredRule } from '@carboledger/ui';
import { useTranslations } from '@/hooks';

/**
 * Props:
 *   rules            RHF rules; each slice points at its own positive-number rule
 *   capsByUnit       unit symbol → its ceiling; the field reads the picked unit itself
 *   buildRules       (cap) => rules — the slice keeps its own messages
 *   placeholder      overrides the shared "Enter the shipment quantity" copy
 *   fieldName        form key, so a screen with several shipment rows can index them
 *   label            overrides the shared label, e.g. to trail it with a glyph
 *   requiredMessage  replaces Field's generic required copy where a screen specifies its own
 *   required         defaults to true; a conditionally-required quantity passes false
 *   disabled         locked once something downstream depends on it, or a picked record fills it
 */
export const ShipmentQuantityField = ({
  rules,
  capsByUnit,
  buildRules,
  placeholder,
  fieldName = 'shipmentQuantity',
  label,
  requiredMessage,
  required = true,
  disabled = false,
}) => {
  const t = useTranslations();
  const { values } = useFormState();

  // The cap follows the unit the form is in, so the caller passes both and never the rule.
  const capped = useMemo(
    () => (buildRules ? buildRules(capsByUnit?.[values?.shipmentQuantityUnit]) : rules),
    [buildRules, capsByUnit, values?.shipmentQuantityUnit, rules],
  );

  // Field puts its own required rule first, so a custom message has to replace it, not follow it.
  const fieldRules = useMemo(
    () => (requiredMessage ? [requiredRule(requiredMessage), ...(capped ?? [])] : capped),
    [requiredMessage, capped],
  );

  return (
    <Field name={fieldName} required={required && !requiredMessage} rules={fieldRules}>
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={name}
          label={label ?? t('FIELD_SHIPMENT_QUANTITY')}
          placeholder={placeholder ?? t('SHIPMENT_FORM_SHIPMENT_QUANTITY_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={required}
          disabled={disabled}
        />
      )}
    </Field>
  );
};
